import {Action, Module, Mutation, VuexModule} from "vuex-module-decorators";
import {AxiosResponse} from "axios";
import {UnitSystem} from "@/interfaces/units";
import {$http, $httpLea} from "@/main";
import {IBaseScenario} from "@/lib/types/IBaseScenario";
import {IPxError, processReason} from "@/lib/util/errors";
import {createNewPerRoadScenario} from "@/modules/perRoad/lib/createNew";
import {IPerRoadScenarioImperial, IPerRoadScenarioMetric} from "@/modules/perRoad/interfaces/perRoad";
import {
  perRoadScenarioImperialToMetric,
  perRoadScenarioMetricToImperial,
} from "@/modules/perRoad/lib/unitConversion/perRoadConversion";
import {IWesleaConfig, IWesleaConfigBatch} from "@/modules/perRoad/interfaces/monteCarlo";

const endpoint = "perroad";


@Module({
  name: "perroad",
  namespaced: true,
})

export default class PerRoadStore extends VuexModule {
  /*
   Unit system
   */

  displayUnits: UnitSystem = UnitSystem.Imperial;

  @Mutation
  setDisplayUnits(targetUnitSystem: UnitSystem) {
    this.displayUnits = targetUnitSystem;
  }


  /*
   A central location for http httpErrors to go
   */
  httpErrors: IPxError[] = [];

  @Mutation
  pushHttpErrors(reason: { response: AxiosResponse }) {
    this.httpErrors = processReason(reason);
  }

  @Mutation
  pushError(error: IPxError) {
    this.httpErrors.push(error);
  }

  @Mutation
  clearErrors() {
    this.httpErrors = [];
  }


  /*
   LCCA Models
   */

  currentImperial: IPerRoadScenarioImperial = createNewPerRoadScenario(null);

  get currentMetric(): any {
    return perRoadScenarioImperialToMetric(this.currentImperial);
  }

  get currentScenario(): IPerRoadScenarioImperial | IPerRoadScenarioMetric {
    console.log("Fetching perroad with units:", this.displayUnits);
    let s: any;
    if (this.displayUnits === UnitSystem.Imperial) {
      s = this.currentImperial;
    } else {
      s = this.currentMetric;
    }
    return s;
  }

  @Mutation
  setCurrentScenario(scenario: IPerRoadScenarioImperial | IPerRoadScenarioMetric) {
    console.log("Setting PerRoad scenario", scenario);


    if (scenario.unitSystem === UnitSystem.Imperial) {
      this.currentImperial = scenario;
    } else {
      console.warn("No metric scenario available in PerRoad store, using imperial");
      this.currentImperial = perRoadScenarioMetricToImperial(scenario);
    }
  }

  @Action({rawError: true})
  saveNewScenario(scenario: IPerRoadScenarioImperial) {
    scenario.displayUnits = this.displayUnits;

    // return this.currentImperial
    return $http.post(`project/${endpoint}/new`, scenario).then((response) => {
      this.setCurrentScenario(response.data);
      return response.data;
    }).catch((reason) => {
      console.log(reason);
      this.pushHttpErrors(reason);
      return null;
    });
  }

  @Action({rawError: true})
  loadCurrentScenario(id: number) {
    console.log(`fetching ${endpoint}`, id);

    return $http.get(`project/${endpoint}/${id}`).then((response) => {
      this.setCurrentScenario(response.data);
      return response.data;
    }).catch((reason) => this.pushHttpErrors(reason));
  }

  @Action({rawError: true})
  loadScenario(id: number) {
    return $http.get(`project/${endpoint}/${id}`).then((response) => response.data).catch((reason) => this.pushHttpErrors(reason));
  }

  @Action({rawError: true})
  saveCurrentScenario() {
    this.currentImperial.displayUnits = this.displayUnits;
    $http.put(`project/${endpoint}/${this.currentImperial.id}`, this.currentImperial).then(
      (response) => {
        this.setCurrentScenario(response.data);
      },
    ).catch((reason) => this.pushHttpErrors(reason));
  }

  @Action
  deleteScenario(scenario: IBaseScenario) {
    return $http.delete(`/project/${endpoint}/${scenario.id}`).catch((reason) => this.pushHttpErrors(reason));
  }


  /**
   * Layered Elastic Analysis
   */

  @Action({rawError: true})
  runOneSimulation(config: IWesleaConfig) {
    // console.log('Simulating');
    const runStart = performance.now();
    return $httpLea.post("/", config).then((response) => {
      // console.log('LEA response', response);
      console.log(`Finished: ${performance.now() - runStart} ms`);
      return JSON.parse(response.data);
    }).catch((reason) => this.pushHttpErrors(reason));
  }

  @Action({rawError: true})
  runManySimulations(config: IWesleaConfigBatch) {
    const runStart = performance.now();
    return $httpLea.post("/batch", config).then((response) => {
      // console.log('LEA response', response);
      // console.log(`Finished: ${performance.now() - runStart} ms`);
      // console.log(response.data);
      return response.data;
    }).catch((reason) => {
      if (reason.response.status == 503){
        // Handle momentary server failure on solver side
        return {}
      } else {
        this.pushHttpErrors(reason);
      }
    });
  }

  @Action({rawError: true})
  rebootSolver() {
    return $httpLea.get("/reboot");
  }
}


